import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'

import { Skeleton } from '@/components/ui/skeleton'
import { CreativesApi } from '@/services/creatives/creativesApi'
import { lineItemTransparencyUrl, transparencyLanguageFor } from '@/services/political/transparencyNotice'

/**
 * A political line item's transparency notice, written out as a link. The
 * notice is addressed by a code only the line item's creatives carry, so each
 * row reads it off one of them — none yet means no notice to link to.
 */
export function LineItemTransparencyLink({ lineItemId }: { lineItemId: string }) {
  const { t, i18n } = useTranslation()
  const { data: code, isLoading, isError } = useQuery({
    queryKey: ['lineitem-code', lineItemId],
    queryFn: () => CreativesApi.getLineItemCode(lineItemId),
    // The code is the line item's internal id, so it never changes.
    staleTime: Infinity,
  })

  if (isLoading) return <Skeleton className="h-4 w-36" />
  if (isError || !code) {
    return (
      <span className="text-xs text-muted-foreground">
        {isError ? t('lineItems.transparencyNoticeFailed') : t('lineItems.transparencyNoticeNoCreative')}
      </span>
    )
  }

  const url = lineItemTransparencyUrl(code)
  return (
    <a
      href={lineItemTransparencyUrl(code, transparencyLanguageFor(i18n.language))}
      target="_blank"
      rel="noopener noreferrer"
      title={url}
      className="block truncate text-xs text-(--ad-link)"
      // The row itself opens the line item.
      onClick={(e) => e.stopPropagation()}
    >
      {url.replace(/^https?:\/\//, '')}
    </a>
  )
}
